import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@clerk/clerk-react';
import { createFileRoute } from '@tanstack/react-router';
import { portalAPI } from '@/lib/api/portal';
import { WizardStepper } from '@/components/registration/WizardStepper';
import { EventEntryStep } from '@/components/registration/EventEntryStep';
import { TournamentSelectionStep } from '@/components/registration/TournamentSelectionStep';
import { SpecialEventsStep } from '@/components/registration/SpecialEventsStep';
import { AddonsSelectionStep } from '@/components/registration/AddonsSelectionStep';
import { CheckoutStep } from '@/components/registration/CheckoutStep';
import { ConfirmationStep } from '@/components/registration/ConfirmationStep';
import type { RegistrationWizardState, Cart, AddonSelection } from '@/types';

export const Route = createFileRoute('/register')({
  component: RegisterPage,
});

const WIZARD_STEPS = [
  { id: 1, name: 'Event Entry' },
  { id: 2, name: 'Tournaments' },
  { id: 3, name: 'Special Events' },
  { id: 4, name: 'Add-ons' },
  { id: 5, name: 'Checkout' },
  { id: 6, name: 'Confirmation' },
];

interface ExtendedWizardState extends RegistrationWizardState {
  specialEvents: {
    selectedIds: string[];
  };
}

const initialState: ExtendedWizardState = {
  currentStep: 1,
  completedSteps: [],
  cart: {
    items: [],
    subtotal: 0,
    tax: 0,
    total: 0,
  },
  eventRegistration: {
    wantsToPay: false,
  },
  tournaments: {
    selectedIds: [],
  },
  specialEvents: {
    selectedIds: [],
  },
  addons: {
    selections: [],
  },
};

function RegisterPage() {
  const { isLoaded } = useUser();
  const [wizardState, setWizardState] = useState<ExtendedWizardState>(initialState);

  // Fetch flow config to determine which steps to show
  const { data: flowConfig, isLoading: flowConfigLoading } = useQuery({
    queryKey: ['flowConfig'],
    queryFn: () => portalAPI.getFlowConfig(),
  });

  // Fetch event for countdown/blocking
  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ['event'],
    queryFn: () => portalAPI.getEvent(),
  });

  const handleStepComplete = useCallback((step: number, data?: Partial<ExtendedWizardState>) => {
    setWizardState(prev => {
      const newState = {
        ...prev,
        ...data,
        completedSteps: [...new Set([...prev.completedSteps, step])],
        currentStep: step + 1,
      };
      return newState;
    });
  }, []);

  const handleStepBack = useCallback(() => {
    setWizardState(prev => ({
      ...prev,
      currentStep: Math.max(1, prev.currentStep - 1),
    }));
  }, []);

  const handleCartUpdate = useCallback((cart: Cart) => {
    setWizardState(prev => ({
      ...prev,
      cart,
    }));
  }, []);

  const handleTournamentsChange = useCallback((selectedIds: string[]) => {
    setWizardState(prev => ({
      ...prev,
      tournaments: { selectedIds },
    }));
  }, []);

  const handleSpecialEventsChange = useCallback((selectedIds: string[]) => {
    setWizardState(prev => ({
      ...prev,
      specialEvents: { selectedIds },
    }));
  }, []);

  const handleAddonsChange = useCallback((selections: AddonSelection[]) => {
    setWizardState(prev => ({
      ...prev,
      addons: { selections },
    }));
  }, []);

  if (flowConfigLoading || eventLoading || !isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse text-muted-foreground">Loading registration...</div>
      </div>
    );
  }

  // Determine which steps are enabled
  const enabledSteps = WIZARD_STEPS.filter(step => {
    const config = flowConfig?.wizardSteps?.find(s => s.stepId === step.id);
    return config?.enabled !== false;
  });

  const renderCurrentStep = () => {
    switch (wizardState.currentStep) {
      case 1:
        return (
          <EventEntryStep
            flowConfig={flowConfig}
            wizardState={wizardState}
            onComplete={(data) => handleStepComplete(1, data)}
          />
        );
      case 2:
        return (
          <TournamentSelectionStep
            selectedIds={wizardState.tournaments.selectedIds}
            onChange={handleTournamentsChange}
            onComplete={() => handleStepComplete(2)}
            onBack={handleStepBack}
          />
        );
      case 3:
        return (
          <SpecialEventsStep
            selectedIds={wizardState.specialEvents.selectedIds}
            onChange={handleSpecialEventsChange}
            onComplete={() => handleStepComplete(3)}
            onBack={handleStepBack}
            isEventRegistrant={wizardState.eventRegistration.entryType === 'paid'}
          />
        );
      case 4:
        return (
          <AddonsSelectionStep
            selections={wizardState.addons.selections}
            onChange={handleAddonsChange}
            onComplete={() => handleStepComplete(4)}
            onBack={handleStepBack}
          />
        );
      case 5:
        return (
          <CheckoutStep
            wizardState={wizardState}
            onCartUpdate={handleCartUpdate}
            onComplete={() => handleStepComplete(5)}
            onBack={handleStepBack}
          />
        );
      case 6:
        return (
          <ConfirmationStep
            wizardState={wizardState}
            event={event}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container py-8 space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-heading font-bold">Event Registration</h1>
        <p className="text-muted-foreground">
          Complete the steps below to register for the event
        </p>
      </div>

      <WizardStepper
        steps={enabledSteps}
        currentStep={wizardState.currentStep}
        completedSteps={wizardState.completedSteps}
      />

      <div className="max-w-3xl mx-auto">
        {renderCurrentStep()}
      </div>
    </div>
  );
}
